import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check } from "@phosphor-icons/react";
import { cn } from "@/lib/cn";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { dropdownContentCollisionClassName, floatingCollisionPadding, floatingLayerClassName } from "@/components/ui/overlays/overlayPositioning";
import type { ActionMenuProps } from "./ActionMenu.types";

const itemSizeClassName = {
  xs: "bfrs-px-2.5 bfrs-py-1.5 bfrs-text-[12px]",
  sm: "bfrs-px-3 bfrs-py-2 bfrs-text-xs",
  md: "bfrs-px-3.5 bfrs-py-2.5 bfrs-text-sm",
  lg: "bfrs-px-4 bfrs-py-3 bfrs-text-sm"
} as const;

export function ActionMenu({ trigger, items, align = "end", side = "bottom", size = "md", className }: ActionMenuProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <BfrsPortalScope>
          <DropdownMenu.Content
            align={align}
            side={side}
            sideOffset={6}
            collisionPadding={floatingCollisionPadding}
            className={cn(floatingLayerClassName, "bfrs-min-w-48 bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-p-1 bfrs-shadow-elevated bfrs-animate-content-in", dropdownContentCollisionClassName, className)}
          >
            {items.map((item, index) => {
              if (item.divider) return <DropdownMenu.Separator key={item.id ?? `divider-${index}`} className="bfrs-my-1 bfrs-h-px bfrs-bg-border" />;
              if (item.section) {
                return (
                  <DropdownMenu.Label key={item.id ?? `section-${index}`} className="bfrs-px-3 bfrs-py-1.5 bfrs-text-[10px] bfrs-font-bold bfrs-uppercase bfrs-tracking-[0.08em] bfrs-text-text-muted">
                    {item.section}
                  </DropdownMenu.Label>
                );
              }

              return (
                <DropdownMenu.Item
                  key={item.id ?? index}
                  disabled={item.disabled}
                  onSelect={item.onSelect}
                  className={cn(
                    "bfrs-flex bfrs-cursor-default bfrs-select-none bfrs-items-center bfrs-gap-2 bfrs-rounded-md bfrs-text-text-secondary bfrs-outline-none data-[disabled]:bfrs-pointer-events-none data-[highlighted]:bfrs-bg-primary-soft data-[disabled]:bfrs-text-text-disabled",
                    itemSizeClassName[size],
                    item.destructive && "bfrs-text-danger"
                  )}
                >
                  <span className="bfrs-flex bfrs-size-4 bfrs-shrink-0 bfrs-items-center bfrs-justify-center">{item.checked ? <Check size={14} weight="bold" /> : item.icon}</span>
                  <span className="bfrs-min-w-0 bfrs-flex-1 bfrs-truncate">{item.label}</span>
                  {item.badge && <span className="bfrs-ml-2 bfrs-shrink-0">{item.badge}</span>}
                  {item.shortcut && <span className="bfrs-ml-3 bfrs-shrink-0 bfrs-text-xs bfrs-text-text-muted">{item.shortcut}</span>}
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </BfrsPortalScope>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
